import React, { useEffect, useState, useCallback } from "react";
import { CalendarClock, Plus, Trash2, AlertTriangle, Warehouse, Store } from "lucide-react";
import api from "../lib/api";
import { Card, PageHeader, PageLoader, Badge, Button, Modal, Select } from "../lib/ui";
import { useToast } from "../lib/toast";
import { fmtNum, fmtUnit } from "../lib/constants";
import { ProductAvatar } from "../components/visual";
import { useAuth } from "../context/AuthContext";
import { useT } from "../lib/i18n";

const STATE_META = {
  EXPIRED: { tone: "danger", label: "Expired" },
  EXPIRING_7: { tone: "danger", label: "Expiring ≤7d" },
  EXPIRING_30: { tone: "warn", label: "Expiring ≤30d" },
  OK: { tone: "success", label: "OK" },
  NONE: { tone: "mute", label: "No date" },
};

export default function Expiry() {
  const toast = useToast();
  const { isRole } = useAuth();
  const { t } = useT();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("ALL");
  const [products, setProducts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ product_id: "", branch_id: "", batch_number: "", expiry_date: "", quantity: "" });

  const canEdit = isRole("SUPER_ADMIN", "WAREHOUSE", "CENTRAL_PURCHASING");

  const load = useCallback(() => {
    const qs = filter === "ALL" ? "" : `?status=${filter}`;
    api.get(`/batches${qs}`).then(({ data }) => setData(data));
  }, [filter]);
  useEffect(() => {
    load();
    api.get("/products?active_only=true").then(({ data }) => setProducts(data));
    api.get("/branches").then(({ data }) => setBranches(data));
  }, [load]);

  const addBatch = async () => {
    if (!form.product_id || !form.expiry_date) return toast.error(t("Product and expiry date are required"));
    try {
      await api.post("/batches", { ...form, branch_id: form.branch_id || null, quantity: Number(form.quantity || 0) });
      toast.success(t("Batch added")); setModal(false);
      setForm({ product_id: "", branch_id: "", batch_number: "", expiry_date: "", quantity: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("Remove this batch record?"))) return;
    try { await api.delete(`/batches/${id}`); toast.success(t("Removed")); load(); }
    catch (e) { toast.error(t("Failed")); }
  };

  if (!data) return <PageLoader />;
  const s = data.summary;

  return (
    <div className="animate-fade-up pb-24 lg:pb-2">
      <PageHeader title={t("Expiry Management")} subtitle={t("Batch & expiry tracking — expired, ≤7 days, ≤30 days")} icon={CalendarClock}>
        {canEdit && <Button onClick={() => setModal(true)} data-testid="batch-add-btn"><Plus size={18} /> {t("Add Batch")}</Button>}
      </PageHeader>

      <div className="mb-5 grid grid-cols-3 gap-4">
        <SummaryTile label={t("Expired")} value={s.expired} tone="danger" onClick={() => setFilter("EXPIRED")} testid="expiry-expired" />
        <SummaryTile label={t("Expiring ≤7 Days")} value={s.expiring_7} tone="danger" onClick={() => setFilter("EXPIRING_7")} testid="expiry-7" />
        <SummaryTile label={t("Expiring ≤30 Days")} value={s.expiring_30} tone="warn" onClick={() => setFilter("EXPIRING_30")} testid="expiry-30" />
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {["ALL", "EXPIRED", "EXPIRING_7", "EXPIRING_30", "OK"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`expiry-filter-${f}`}
            className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition-all ${filter === f ? "bg-primary text-white" : "bg-white border border-line text-ink-soft hover:bg-stone-50"}`}>
            {STATE_META[f]?.label ? t(STATE_META[f].label) : t("All")}
          </button>
        ))}
      </div>

      {data.batches.length === 0 ? (
        <Card className="p-10 text-center text-ink-mute" data-testid="expiry-empty">{t("No batches in this view. Batch & expiry are captured on stock-in / PO receive, or add manually.")}</Card>
      ) : (
        <Card className="overflow-hidden p-0">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-line text-left text-xs uppercase text-ink-mute">
              <th className="px-5 py-3">{t("Product")}</th><th className="px-5 py-3">{t("Location")}</th><th className="px-5 py-3">{t("Batch")}</th>
              <th className="px-5 py-3">{t("Expiry")}</th><th className="px-5 py-3 text-right">{t("Qty")}</th>
              <th className="px-5 py-3 text-center">{t("Status")}</th>{canEdit && <th className="px-5 py-3" />}</tr></thead>
            <tbody>
              {data.batches.map((b) => {
                const meta = STATE_META[b.expiry_state] || STATE_META.NONE;
                return (
                  <tr key={b.id} className="border-b border-line/60 hover:bg-stone-50/60" data-testid={`batch-row-${b.product_name}`}>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3"><ProductAvatar image={b.image} name={b.product_name} size="sm" /><span className="font-semibold">{b.product_name}</span></div>
                    </td>
                    <td className="px-5 py-3"><span className="inline-flex items-center gap-1.5 text-xs text-ink-soft">{b.branch_id ? <Store size={13} /> : <Warehouse size={13} />}{b.location}</span></td>
                    <td className="px-5 py-3 font-mono text-xs">{b.batch_number || "—"}</td>
                    <td className="px-5 py-3">{b.expiry_date || "—"}</td>
                    <td className="px-5 py-3 text-right">{fmtNum(b.quantity)} {fmtUnit(b.unit)}</td>
                    <td className="px-5 py-3 text-center"><Badge tone={meta.tone}>{t(meta.label)}</Badge></td>
                    {canEdit && <td className="px-5 py-3"><button onClick={() => remove(b.id)} className="rounded-lg p-1.5 text-danger hover:bg-danger-light" data-testid={`batch-del-${b.product_name}`}><Trash2 size={14} /></button></td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      <Modal open={modal} onClose={() => setModal(false)} title={t("Add Batch")}
        footer={<><Button variant="outline" onClick={() => setModal(false)}>{t("Cancel")}</Button><Button onClick={addBatch} data-testid="batch-save">{t("Add Batch")}</Button></>}>
        <Select label={t("Product")} value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })} className="mb-3" data-testid="batch-product">
          <option value="">{t("Select product...")}</option>
          {products.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.unit})</option>)}
        </Select>
        <Select label={t("Location")} value={form.branch_id} onChange={(e) => setForm({ ...form, branch_id: e.target.value })} className="mb-3" data-testid="batch-location">
          <option value="">{t("Central Warehouse")}</option>
          {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </Select>
        <div className="grid grid-cols-3 gap-3">
          <div><label className="cf-label">{t("Batch No.")}</label><input value={form.batch_number} onChange={(e) => setForm({ ...form, batch_number: e.target.value })} className="cf-input" data-testid="batch-number" /></div>
          <div><label className="cf-label">{t("Expiry Date")}</label><input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} className="cf-input" data-testid="batch-expiry" /></div>
          <div><label className="cf-label">{t("Quantity")}</label><input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="cf-input" data-testid="batch-qty" /></div>
        </div>
      </Modal>
    </div>
  );
}

function SummaryTile({ label, value, tone, onClick, testid }) {
  const tones = { danger: "bg-danger-light text-danger-text", warn: "bg-warn-light text-warn-text" };
  return (
    <button onClick={onClick} data-testid={testid} className="flex items-center gap-3 rounded-2xl border border-line bg-white p-4 text-left transition-all hover:-translate-y-0.5 hover:shadow-cardhover">
      <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tones[tone]}`}><AlertTriangle size={20} /></div>
      <div><p className="font-heading text-2xl font-bold leading-none text-ink">{value}</p><p className="mt-1 text-xs font-medium text-ink-soft">{label}</p></div>
    </button>
  );
}
